/**
 * Shadows cast by sticky chrome inside a scroll root (a section head, a table head, a footer, a
 * column group pinned at a side) only while it is stuck, never while it sits where the layout put
 * it; and, with an empty sticky host at either end of the scroller (`topEdge`, `bottomEdge`), the
 * scroll shadows of the scroller itself. Unlike `ScrollShadowWrapper` it needs no JS measuring the
 * scroller, and when the content scrolls away under sticky chrome the shadow falls under the
 * chrome that is stuck, not at the scroller's edge.
 *
 * Pure CSS: the sticky element is a `scroll-state` container (`host`) and the shadow an empty
 * `aria-hidden` child of it (a container query styles what is inside, never the container itself).
 * It goes with its host, so the next head pushing a stuck one out covers the shadow instead of
 * being cut across by it.
 *
 * The scroller's own edges (`topEdgeShadow`, `bottomEdgeShadow`) are laid over the scroller's whole
 * box instead, anchored to it (`scroller`): a shadow laid out inside the scroller would stop where
 * its scroll bar begins, the bar being outside the box its content can paint in.
 *
 * Written to the standard on purpose (CSS Conditional Rules 5, `scroll-state` container queries):
 * where they are not known yet (Safari, Firefox as of 2026-10) the rule is left out and nothing
 * casts a shadow; nothing else changes, the content scrolls and reads the same. A scroll shadow is
 * a hint, not something the page depends on, so it waits for the browsers to catch up instead of
 * being polyfilled with JS that measures the scroller.
 *
 * A stuck element's shadow is a hairline in the border colour on its edge and a gradient off it,
 * as `ScrollShadowWrapper`'s `edge="shadow"`; the scroller's own edges have the gradient only, as
 * they lie on its frame's border, which would be a second line beside the hairline. The gradient is dark in both themes: black at 5% on a
 * light sheet, stronger on a dark one, where 5% doesn't show. Not the foreground colour, which is
 * light in the dark theme and would glow.
 *
 * @example
 * <div className={cn('sticky top-0 z-10', stickyShadows.host)}>
 *   <span aria-hidden className={stickyShadows.stuckTop} />
 *   …
 * </div>
 */
export const stickyShadows = {
  /** On the sticky element (it is positioned already, so the shadow is laid out against it). */
  host: '[container-type:scroll-state]',
  /**
   * On the scroller whose edges cast shadows (`topEdgeShadow`, `bottomEdgeShadow`): what they are
   * anchored to, the name scoped to it, so each scroller's edges find their own. In a frame with
   * round corners, set `--sticky-shadows-radius` to its radius: laid over the frame rather than
   * inside it, the edges are not clipped to its corners and round them off themselves.
   */
  scroller:
    '[anchor-name:--sticky-shadows-scroller] [anchor-scope:--sticky-shadows-scroller]',
  /**
   * The scroller's own top edge, for content with no sticky chrome there: an empty host as the
   * content's first child, stuck once something has scrolled away above; with `topEdgeShadow` in
   * it, it is the scroll shadow of that edge.
   */
  topEdge: 'sticky top-0 z-10 h-0 [container-type:scroll-state]',
  /**
   * The scroller's own bottom edge, for content with no sticky chrome there: an empty host as the
   * content's last child, stuck while there is more below; with `bottomEdgeShadow` in it, it is
   * the scroll shadow of that edge. It sticks a pixel past the edge: content of a fractional
   * height leaves the host a fraction of a pixel off its place at the scroller's end, which
   * counts as stuck, and the shadow would stay where nothing is left to scroll (the top one
   * stands exactly at its place at the start, the scroll being 0 there).
   */
  bottomEdge: 'sticky -bottom-px z-10 h-0 [container-type:scroll-state]',
  /** Stuck to the top (`sticky top-…`): the shadow falls below it, over what scrolls under. */
  stuckTop:
    'pointer-events-none absolute inset-x-0 top-full h-1.5 border-t border-border-default bg-gradient-to-b from-black/5 to-transparent dark:from-black/40 opacity-0 transition-opacity duration-100 [@container_scroll-state(stuck:_top)]:opacity-100',
  /**
   * On a host stuck to the top that can't hold the shadow as a child (a table head, whose cells
   * are not queried against it), with `host`: its own `::before` is the shadow, a gradient only,
   * the host drawing its own line.
   */
  stuckTopBefore:
    'before:pointer-events-none before:absolute before:inset-x-0 before:top-full before:h-1.5 before:bg-gradient-to-b before:from-black/5 before:to-transparent dark:before:from-black/40 before:opacity-0 before:transition-opacity before:duration-100 [@container_scroll-state(stuck:_top)]:before:opacity-100',
  /**
   * Stuck to the bottom (`sticky bottom-…`): the shadow falls above it. Chrome that ends the
   * content sticks at `-bottom-px`, as `bottomEdge`, or its shadow stays at the end.
   */
  stuckBottom:
    'pointer-events-none absolute inset-x-0 bottom-full h-1.5 border-b border-border-default bg-gradient-to-t from-black/5 to-transparent dark:from-black/40 opacity-0 transition-opacity duration-100 [@container_scroll-state(stuck:_bottom)]:opacity-100',
  /**
   * In `topEdge`: the shadow over the scroller's top edge, across its whole box, its scroll bar
   * included. `fixed`, so it is laid out against a box outside the scroller and is not clipped to
   * the scroller's content (a clipping ancestor clips it only if it is that box: transformed or
   * contained). Needs `scroller` on the scroller.
   */
  topEdgeShadow:
    'pointer-events-none fixed [position-anchor:--sticky-shadows-scroller] [top:anchor(top)] [left:anchor(left)] [right:anchor(right)] h-[max(calc(var(--spacing)*1.5),var(--sticky-shadows-radius,0px))] rounded-t-[var(--sticky-shadows-radius,0px)] bg-gradient-to-b bg-[length:100%_calc(var(--spacing)*1.5)] bg-top bg-no-repeat from-black/5 to-transparent dark:from-black/40 opacity-0 transition-opacity duration-100 [@container_scroll-state(stuck:_top)]:opacity-100',
  /** In `bottomEdge`: the shadow over the scroller's bottom edge; see `topEdgeShadow`. */
  bottomEdgeShadow:
    'pointer-events-none fixed [position-anchor:--sticky-shadows-scroller] [bottom:anchor(bottom)] [left:anchor(left)] [right:anchor(right)] h-[max(calc(var(--spacing)*1.5),var(--sticky-shadows-radius,0px))] rounded-b-[var(--sticky-shadows-radius,0px)] bg-gradient-to-t bg-[length:100%_calc(var(--spacing)*1.5)] bg-bottom bg-no-repeat from-black/5 to-transparent dark:from-black/40 opacity-0 transition-opacity duration-100 [@container_scroll-state(stuck:_bottom)]:opacity-100',
  /** Stuck to the left edge (`sticky left-…`): the shadow falls right of it, over what scrolls under. */
  stuckLeft:
    'pointer-events-none absolute inset-y-0 left-full w-1.5 border-l border-border-default bg-gradient-to-r from-black/5 to-transparent dark:from-black/40 opacity-0 transition-opacity duration-100 [@container_scroll-state(stuck:_left)]:opacity-100',
  /** Stuck to the right edge (`sticky right-…`): the shadow falls left of it. */
  stuckRight:
    'pointer-events-none absolute inset-y-0 right-full w-1.5 border-r border-border-default bg-gradient-to-l from-black/5 to-transparent dark:from-black/40 opacity-0 transition-opacity duration-100 [@container_scroll-state(stuck:_right)]:opacity-100'
};
